import React, { FunctionComponent, useState, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import _get from 'lodash/get';
import MaskedInput from 'react-text-mask';

import Icon from '../../Icons';
import { getCardType } from '../promiseAction';
import { cardMapping } from '../constants';
import { parsePrice, formatPrice } from '../../../utils/utils';
import { showLoader } from '../../Loader/action';
import { updateTransaction } from '../promiseAction';
import { processTransaction } from '../promiseAction';
import { redirectToBankPage } from '../processTransactionResponse';
import { formatExpiry } from '../../../utils/utils';
import CvvDescription from './CvvDescription';
import { addMoney } from '../promiseAction';
import { setMoney } from '../action';
import { sendEvent2 } from '../../../utils/tracking';

const Form: FunctionComponent<{
  paymentInfo: any;
  updateInternetCharges: Function;
  type: string;
  amount: number;
  internetCharges: number;
  fromFastag: any;
}> = ({ paymentInfo, updateInternetCharges, type, amount, internetCharges, fromFastag }) => {
  const dispatch = useDispatch();
  const { register, handleSubmit, errors, control } = useForm();
  const inputRef: any = useRef(null);
  const fAmount = formatPrice(amount);

  const [checked, setChecked] = useState(true);
  const [cardType, setCardType] = useState('');
  const [apiError, setApiError] = useState('');

  const dispatchLoader = useCallback((payload: any) => dispatch(showLoader(payload)), [dispatch]);

  const dispatchInitiatePayment = useCallback(
    (payload: any) =>
      dispatch(addMoney({ amount })).then((resp: any) => {
        if (resp.data.success) {
          dispatch(setMoney(resp.data));
          dispatchLoader({ show: false });
        } else {
          payload.retry++;
          if (payload.retry < 4) {
            dispatchInitiatePayment({
              amount,
              retry: payload.retry,
            });
          } else {
            dispatchLoader({ show: false });
            setApiError('Something went wrong. Please try again.');
          }
        }
      }),
    // TODO: handle dependencies amount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, dispatchLoader],
  );

  const dispatchProcessTransaction = useCallback(
    (payload: any) =>
      dispatch(processTransaction(payload.data)).then((resp: any) => {
        const resultInfo = _get(resp, 'data.body.resultInfo', {});
        if (resultInfo.resultStatus === 'S') {
          redirectToBankPage(payload.data.paymentMode, resp);
        } else {
          if (resultInfo && resultInfo.resultMsg) {
            setApiError(resultInfo.resultMsg);
          } else {
            setApiError('Something went wrong');
          }
          dispatchInitiatePayment({ amount: payload.amount, retry: 1 });
        }
      }),
    [dispatch, dispatchInitiatePayment],
  );

  const dispatchUpdateTransaction = useCallback(
    (payload: any) =>
      dispatch(updateTransaction(payload.params)).then((resp: any) => {
        if (resp.data.success) {
          const data: any = {
            requestType: 'NATIVE',
            mId: paymentInfo.merchantId,
            orderId: paymentInfo.transactionCode,
            token: paymentInfo.checkSumHash,
            ...payload.cardInfo,
          };
          dispatchProcessTransaction({ data, amount: payload.params.amount });
        } else {
          dispatchLoader({ show: false });
          setApiError(_get(resp, 'data.message', 'Something went wrong'));
        }
      }),
    [dispatch, paymentInfo, dispatchLoader, dispatchProcessTransaction],
  );

  const validateExpiry = (validity?: string) => validity && formatExpiry(validity).length === 6;

  const onSubmit = (values?: any) => {
    const { cardNumber, cvv, validity } = values;
    dispatchLoader({ show: true });
    const data: any = {
      params: {
        appVersion: 'web',
        paymentMode: cardMapping[cardType],
        serviceProvider: 'PAYTM CUSTOM',
        userCode: paymentInfo.customerId,
        transactionCode: paymentInfo.transactionCode,
        transactionToken: paymentInfo.checkSumHash,
        amount: fAmount,
        pspAmount: parseFloat(formatPrice(internetCharges || 0)),
      },
      cardInfo: {
        storeInstrument: checked ? 1 : 0,
        cardInfo: `|${cardNumber}|${cvv}|${formatExpiry(validity)}`,
        paymentMode: cardType,
      },
    };
    if (fromFastag?.fromFastag === true) {
      sendEvent2(
        'pay_btn',
        {
          event_action: 'click',
          screen_name: 'wallet_recharge_screen',
          miscellaneous: JSON.stringify({
            type: 'cards',
          }),
          target_product: 'fastag',
          event_platform: 'web',
        },
        true,
      );
    }
    dispatchUpdateTransaction(data);
  };

  const dispatchToStore = useCallback(
    (payload) =>
      dispatch(getCardType(payload)).then((resp: any) => {
        console.log(_get(resp, 'data.body.binDetail.paymentMode', ''));
        const cardType = _get(resp, 'data.body.binDetail.paymentMode', '');
        setCardType(cardType);
        updateInternetCharges({ [type]: cardMapping[cardType] });
      }),
    // TODO: handle dependencies 'type' and 'updateInternetCharges'
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, paymentInfo],
  );

  const handleCardNumber = (e: any) => {
    setApiError('');
    const value = e.target.value || '';
    if (!value) {
      setCardType('');
      updateInternetCharges({ [type]: '' });
      return;
    }
    const regExp = new RegExp(/^\d+$/);
    if ((regExp.test(value) && value.length === 6) || value.length === 16) {
      dispatchToStore({
        cardNumber: value,
        mId: paymentInfo.merchantId,
        orderId: paymentInfo.transactionCode,
        token: paymentInfo.checkSumHash,
      });
    }
  };

  return (
    <div className="p16 mode-container">
      <form className="form-container pr8" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-content-s card-info">
          <div className={`info-column card-number ${errors?.cardNumber ? 'error-field' : ''}`}>
            <p className="small mb8 font-light-grey form-label">Card Number</p>
            <input
              type="text"
              placeholder=""
              className="form-input"
              name="cardNumber"
              onChange={(e) => handleCardNumber(e)}
              ref={register({
                required: true,
                pattern: {
                  value: /^\d+$/,
                  message: 'Only digits are allowed',
                },
                maxLength: {
                  value: 16,
                  message: `Max. length should be 16`,
                },
                minLength: {
                  value: 16,
                  message: `Min. length should be 16`,
                },
              })}
            />
            <p className="form-error absolute">{errors?.cardNumber && errors?.cardNumber?.message}</p>
          </div>
          <div className={`info-column ${errors?.validity ? 'error-field' : ''}`}>
            <p className="small mb8 font-light-grey form-label">Valid thru</p>
            <Controller
              as={<MaskedInput ref={inputRef} mask={[/[0,1]/, /[0,1,2,3,4,5,6,7,8,9]/, '/', /\d/, /\d/]} />}
              type="text"
              placeholder="MM/YY"
              className="form-input"
              guide={false}
              onFocus={() => {
                inputRef?.current?.inputElement.focus();
              }}
              control={control}
              name="validity"
              rules={{ required: true, validate: (value) => (validateExpiry(value) ? true : 'Invalid Date') }}
            />
            <p className="form-error absolute">{errors?.validity && errors?.validity?.message}</p>
          </div>
          <div className={`info-column ${errors.cvv ? 'error-field' : ''}`}>
            <p className="small mb8 font-light-grey form-label">
              CVV <CvvDescription />
            </p>
            <input
              type="password"
              placeholder="123"
              className="form-input"
              name="cvv"
              ref={register({
                required: true,
                maxLength: {
                  value: 3,
                  message: `Max. length should be 3`,
                },
                minLength: {
                  value: 3,
                  message: `Min. length should be 3`,
                },
                pattern: {
                  value: /^\d+$/,
                  message: 'Only digits are allowed',
                },
              })}
            />
            <p className="form-error absolute">{errors?.cvv && errors?.cvv?.message}</p>
          </div>
          <div className={`info-column name ${errors?.name ? 'error-field' : ''}`}>
            <p className="small mb8 font-light-grey form-label">Name on Card</p>
            <input type="text" placeholder="" className="form-input" name="name" ref={register({ required: true })} />
            <p className="form-error absolute">{errors?.name && errors?.name?.message}</p>
          </div>
          <div className="info-column">
            <input
              className="form-btn"
              type="submit"
              value={`Pay ${parsePrice({ price: amount + internetCharges })}`}
            />
          </div>
        </div>
        {apiError && <p className={`form-error absolute ${apiError ? '' : 'none'}`}>{apiError}</p>}
        <div className="input-checkbox mt16 flex falign-c">
          <input
            type="checkbox"
            name="saveCard"
            id="save-card"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span>{checked ? <Icon name="checked" /> : null}</span>
          <label htmlFor="save-card">Save this card for future transactions</label>
        </div>
      </form>
    </div>
  );
};

export default Form;
